import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createFeeDistributionRecord } from 'src/shared/services/feeDistributionService';
import PaymentPageTabNavigation from '../components/paymentTabNavigation';
import { FeeDistributionRecordsTable } from '../components/feeDistributionRecordsTable';
import { useFetchFeeDistribution } from '../hooks/useFetchFeeDistribution';
import { useFetchFeeDistributionRecords } from '../hooks/useFetchFeeDistributionRecords';
import { FeeDistributionRecipient, FeeDistributionSearchFilter } from '../models/feeDistribution';

/**
 * Fördelningsunderlag.
 *
 * Sidan visar hur mycket av de bokförda inbetalningarna som hör till förbundet, varje distrikt och
 * varje förening. Bara betalningar med verifikationsnummer räknas, alltså de som redan ingår i ett
 * bokföringsunderlag. Inget betalas ut härifrån, det kommer i ett senare steg.
 */

const formatDate = (date: Date) => {
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);

  return `${date.getFullYear()}-${month}-${day}`;
};

export const PaymentDistributionPage = () => {
  const { t } = useTranslation();

  const [ownerId, setOwnerId] = useState<number | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string>(formatDate(new Date(new Date().getFullYear(), 0, 1)));
  const [dateTo, setDateTo] = useState<string>(formatDate(new Date()));

  const searchFilter: FeeDistributionSearchFilter = useMemo(() => {
    return { ownerId: ownerId ?? 0, dateFrom, dateTo };
  }, [ownerId, dateFrom, dateTo]);

  const { feeDistribution, isLoading, fetchData } = useFetchFeeDistribution(searchFilter);
  const { records, fetchData: fetchRecords } = useFetchFeeDistributionRecords(ownerId);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const createRecord = async () => {
    setIsCreating(true);

    try {
      await createFeeDistributionRecord(searchFilter);
      toast.success(<>{t('invoice:paymentDistribution.createRecordSuccess')}</>);
      setConfirmOpen(false);
      await fetchRecords();
      await fetchData();
    } catch (error) {
      ConsoleHelper.log(error);
      toast.error(<>{t('invoice:paymentDistribution.createRecordError')}</>);
    } finally {
      setIsCreating(false);
    }
  };

  const districts = feeDistribution?.recipients.filter((x) => x.level === 'distrikt') ?? [];
  const locals = feeDistribution?.recipients.filter((x) => x.level === 'förening') ?? [];

  const hasOwner = !!ownerId && ownerId > 0;
  const hasRows = !!feeDistribution && feeDistribution.paymentCount > 0;

  const accountText = (row: FeeDistributionRecipient) => {
    if (row.bankGiro) return t('invoice:paymentDistribution.accountBankGiro', { value: row.bankGiro });
    if (row.postGiro) return t('invoice:paymentDistribution.accountPostGiro', { value: row.postGiro });

    return undefined;
  };

  const summaryCard = (label: string, value: number, sub: string) => (
    <Box key={label} sx={{ flex: 1, minWidth: 200, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.4 }}>
        <AmountWithCurrency amount={value} decimals={0} />
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {sub}
      </Typography>
    </Box>
  );

  const recipientRows = (rows: FeeDistributionRecipient[], groupLabel: string) => (
    <>
      <TableRow>
        <TableCell colSpan={6} sx={{ backgroundColor: (thm) => thm.palette.action.hover, fontWeight: 600 }}>
          {groupLabel}
        </TableCell>
      </TableRow>
      {rows.map((row) => {
        const account = accountText(row);

        return (
          <TableRow key={`${row.level}-${row.recipientId}`}>
            <TableCell>{row.name || <em>{t('invoice:paymentDistribution.recipientMissing')}</em>}</TableCell>
            <TableCell>{row.orgNr}</TableCell>
            <TableCell>{account || <em>{t('invoice:paymentDistribution.accountMissing')}</em>}</TableCell>
            <TableCell align='right'>{row.paymentCount}</TableCell>
            <TableCell align='right'>
              <AmountWithCurrency amount={row.amount} />
            </TableCell>
            <TableCell>
              {account ? (
                <CustomChip size='small' color='success' label={t('invoice:paymentDistribution.statusIncluded')} smallRadius />
              ) : (
                <CustomChip size='small' color='warning' label={t('invoice:paymentDistribution.statusNoAccount')} smallRadius />
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );

  return (
    <GridWrapper>
      <Grid item xs={12} md={4}>
        <MainContentHeader title={t('invoice:paymentDistribution.pageTitle')} />
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <PaymentPageTabNavigation />

          <PageFilterSection
            leftContent={
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <DatePicker
                  format='YYYY-MM-DD'
                  label={t('invoice:paymentDistribution.dateFrom')}
                  value={dayjs(dateFrom)}
                  onChange={(date) => {
                    if (date && date.isValid()) setDateFrom(date.format('YYYY-MM-DD'));
                  }}
                  slots={{
                    textField: (params) => <TextField {...params} size='small' InputLabelProps={{ shrink: true }} />,
                  }}
                />
                <DatePicker
                  format='YYYY-MM-DD'
                  label={t('invoice:paymentDistribution.dateTo')}
                  value={dayjs(dateTo)}
                  onChange={(date) => {
                    if (date && date.isValid()) setDateTo(date.format('YYYY-MM-DD'));
                  }}
                  slots={{
                    textField: (params) => <TextField {...params} size='small' InputLabelProps={{ shrink: true }} />,
                  }}
                />
                <Tooltip title={t('invoice:paymentDistribution.infoText')}>
                  <InfoOutlined fontSize='small' color='action' />
                </Tooltip>
              </Box>
            }
            rightContent={<InvoiceGroups callback={(id) => setOwnerId(id)} autoSelect ownerId={ownerId} />}
          />

          {!hasOwner && (
            <Alert severity='info'>
              <AlertTitle>{t('invoice:transactionPage.selectOwnerIdWarningTitle')}</AlertTitle>
              {t('invoice:transactionPage.selectOwnerIdWarningText')}
            </Alert>
          )}

          {hasOwner && isLoading && (
            <Box sx={{ p: 2 }}>
              <Skeleton variant='rectangular' height={90} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant='text' sx={{ height: '40px' }} />
              <Skeleton variant='text' sx={{ height: '40px' }} />
              <Skeleton variant='text' sx={{ height: '40px' }} />
            </Box>
          )}

          {hasOwner && !isLoading && !hasRows && (
            <Alert severity='info'>
              <AlertTitle>{t('invoice:paymentDistribution.noRowsTitle')}</AlertTitle>
              {t('invoice:paymentDistribution.noRowsText')}
            </Alert>
          )}

          {hasOwner && !isLoading && hasRows && feeDistribution && (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2 }}>
                {summaryCard(
                  t('invoice:paymentDistribution.totalPaid'),
                  feeDistribution.totalPaid,
                  t('invoice:paymentDistribution.paymentsCount', { count: feeDistribution.paymentCount }),
                )}
                {summaryCard(
                  t('invoice:paymentDistribution.countryPart'),
                  feeDistribution.countryAmount,
                  t('invoice:paymentDistribution.countryPartInfo'),
                )}
                {summaryCard(
                  t('invoice:paymentDistribution.districtPart'),
                  feeDistribution.districtAmount,
                  t('invoice:paymentDistribution.recipientsCount', { count: districts.length }),
                )}
                {summaryCard(
                  t('invoice:paymentDistribution.localPart'),
                  feeDistribution.localAmount,
                  t('invoice:paymentDistribution.recipientsCount', { count: locals.length }),
                )}
              </Box>

              {feeDistribution.paymentsWithoutDistribution > 0 && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Alert severity='warning'>
                    <AlertTitle>{t('invoice:paymentDistribution.withoutDistributionTitle')}</AlertTitle>
                    {t('invoice:paymentDistribution.withoutDistributionText', {
                      count: feeDistribution.paymentsWithoutDistribution,
                    })}
                  </Alert>
                </Box>
              )}

              <Divider />

              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton variant='contained' disableElevation loading={isCreating} disabled={isCreating} onClick={() => setConfirmOpen(true)}>
                  {t('invoice:paymentDistribution.createRecord')}
                </LoadingButton>
              </Box>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('invoice:paymentDistribution.recipient')}</TableCell>
                    <TableCell>{t('invoice:paymentDistribution.orgNr')}</TableCell>
                    <TableCell>{t('invoice:paymentDistribution.account')}</TableCell>
                    <TableCell align='right'>{t('invoice:paymentDistribution.payments')}</TableCell>
                    <TableCell align='right'>{t('invoice:paymentDistribution.amount')}</TableCell>
                    <TableCell>{t('invoice:paymentDistribution.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {districts.length > 0 && recipientRows(districts, t('invoice:paymentDistribution.districts'))}
                  {locals.length > 0 && recipientRows(locals, t('invoice:paymentDistribution.locals'))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      </Grid>

      {hasOwner && records.length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('invoice:paymentDistribution.earlierRecords')}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              {t('invoice:paymentDistribution.earlierRecordsInfo')}
            </Typography>
            <FeeDistributionRecordsTable records={records} onChanged={fetchRecords} />
          </Paper>
        </Grid>
      )}

      <GenericDialog
        open={confirmOpen}
        maxWidth='sm'
        dialogTitle={t('invoice:paymentDistribution.createRecord')}
        dialogContent={
          <Typography variant='body2'>
            {t('invoice:paymentDistribution.createRecordConfirm', {
              from: dateFrom,
              to: dateTo,
              count: feeDistribution?.paymentCount ?? 0,
            })}
          </Typography>
        }
        dialogActions={
          <>
            <Button variant='outlined' color='primary' disableElevation disabled={isCreating} onClick={() => setConfirmOpen(false)}>
              {t('common:button.cancel')}
            </Button>
            <LoadingButton variant='contained' disableElevation loading={isCreating} disabled={isCreating} onClick={createRecord}>
              {t('common:button.confirm')}
            </LoadingButton>
          </>
        }
        onSave={createRecord}
        onClose={() => {
          if (!isCreating) setConfirmOpen(false);
        }}
      />
    </GridWrapper>
  );
};
